// ============================================================
// Shared Kart Physics — single source of truth for both
// client (Game.ts) and server (Simulation.ts)
// ============================================================

import {
  PlayerInput,
  KartState,
  TrackPoint,
  MAX_SPEED,
  ACCELERATION,
  BRAKE_FORCE,
  FRICTION,
  STEER_SPEED,
  MAX_STEER,
  STEER_RETURN,
  DRIFT_FACTOR,
  GRAVITY,
  RAMP_LANDING_BOOST,
} from './types';
import { config } from './config';
import { checkWallCollision, checkRampGround, findClosestPoint, type RampData } from './track';

// ============================================================
// Track context required by physics simulation
// ============================================================

export interface PhysicsTrackContext {
  path: TrackPoint[];
  roadWidth: number;
  ramps: RampData[];
}

// ============================================================
// simulateKart — pure function for one kart, one tick
//
// Takes a KartState + input + dt, returns a new KartState.
// Both client and server call this identically.
// Does NOT mutate the input kart.
// ============================================================

export function simulateKart(
  kart: KartState,
  input: PlayerInput,
  dt: number,
  track: PhysicsTrackContext | null,
  allKarts?: KartState[],
): KartState {
  const k = { ...kart };

  // ── Timers that tick down regardless ──
  if (k.starTimer > 0) {
    k.starTimer -= dt;
    if (k.starTimer < 0) k.starTimer = 0;
  }
  if (k.shrinkTimer > 0) {
    k.shrinkTimer -= dt;
    if (k.shrinkTimer < 0) k.shrinkTimer = 0;
  }
  if (k.blooperTimer > 0) {
    k.blooperTimer -= dt;
    if (k.blooperTimer < 0) k.blooperTimer = 0;
  }
  if (k.rouletteTimer > 0) {
    k.rouletteTimer -= dt;
    if (k.rouletteTimer < 0) k.rouletteTimer = 0;
  }

  // ── Stunned state ──
  if (k.stunTimer > 0) {
    k.stunTimer -= dt;
    k.speed *= 1 - 3 * dt;
    if (Math.abs(k.speed) < 0.1) k.speed = 0;
    k.steering = 0;
    k.driftTime = 0;
    k.vx = Math.cos(k.yaw) * k.speed;
    k.vz = Math.sin(k.yaw) * k.speed;
    k.x += k.vx * dt;
    k.z += k.vz * dt;
    applyRampGravity(k, dt, track);
    if (k.stunTimer < 0) k.stunTimer = 0;
    return k;
  }

  // ── Steering ──
  if (input.left) {
    k.steering += STEER_SPEED * dt;
  }
  if (input.right) {
    k.steering -= STEER_SPEED * dt;
  }
  k.steering = clamp(k.steering, -MAX_STEER, MAX_STEER);
  if (!input.left && !input.right) {
    k.steering *= 1 - STEER_RETURN * dt;
    if (Math.abs(k.steering) < 0.01) k.steering = 0;
  }

  // ── Speed ──
  if (input.forward) {
    k.speed += ACCELERATION * dt;
  }
  if (input.backward) {
    k.speed -= BRAKE_FORCE * dt;
  }

  // Friction opposes motion
  let frictionMult = 1.0;
  if (k.offRoad) {
    frictionMult = config.offroad.frictionMult;
  }
  if (k.speed > 0) {
    k.speed -= FRICTION * frictionMult * dt;
    if (k.speed < 0) k.speed = 0;
  } else if (k.speed < 0) {
    k.speed += FRICTION * frictionMult * dt;
    if (k.speed > 0) k.speed = 0;
  }

  // ── Compute effective max speed ──
  let maxSpeed = MAX_SPEED;

  // Coin speed bonus: +2% per coin
  maxSpeed *= 1 + k.coins * config.coins.speedBonus;

  // Off-road penalty
  if (k.offRoad) {
    maxSpeed *= config.offroad.speedMult;
  }

  // Shrink penalty
  if (k.shrinkTimer > 0) {
    maxSpeed *= config.specialItems.lightningSpeedMult;
  }

  // Star speed boost
  if (k.starTimer > 0) {
    maxSpeed *= config.specialItems.starSpeedMult;
  }

  // Boost timer affects max speed
  if (k.boostTimer > 0) {
    maxSpeed = Math.max(maxSpeed, MAX_SPEED * config.items.boostMultiplier);
    k.boostTimer -= dt;
    if (k.boostTimer < 0) k.boostTimer = 0;
  }

  k.speed = clamp(k.speed, -MAX_SPEED * 0.3, maxSpeed);

  // Dead zone
  if (Math.abs(k.speed) < 0.1 && !input.forward && !input.backward) {
    k.speed = 0;
  }

  // ── Yaw (only turn when moving) ──
  if (Math.abs(k.speed) > 0.5) {
    k.yaw += k.steering * (k.speed / MAX_SPEED) * dt * 5;
  }

  // ── Velocity ──
  k.vx = Math.cos(k.yaw) * k.speed;
  k.vz = Math.sin(k.yaw) * k.speed;

  // ── Drift: reduce lateral grip + track drift time ──
  const isDrifting = input.drift && Math.abs(k.speed) > MAX_SPEED * 0.5;
  if (isDrifting) {
    const forwardX = Math.cos(k.yaw);
    const forwardZ = Math.sin(k.yaw);
    const rightX = -Math.sin(k.yaw);
    const rightZ = Math.cos(k.yaw);

    const forwardComponent = k.vx * forwardX + k.vz * forwardZ;
    const lateralComponent = k.vx * rightX + k.vz * rightZ;

    // Use surface-dependent drift factor
    let driftFactor = DRIFT_FACTOR;
    // Ice surfaces have more sliding
    if (track) {
      // Ice factor will be applied by the track's surface type later
    }

    const adjustedLateral = lateralComponent * driftFactor;
    k.vx = forwardX * forwardComponent + rightX * adjustedLateral;
    k.vz = forwardZ * forwardComponent + rightZ * adjustedLateral;

    // A1: Accumulate drift charge time
    k.driftTime += dt;
  } else {
    // A1: On drift release, apply tiered boost
    if (k.driftTime > 0) {
      const cfg = config.driftBoost;
      if (k.driftTime >= cfg.purpleThreshold) {
        k.boostTimer = Math.max(k.boostTimer, cfg.purpleDuration);
        if (k.speed < MAX_SPEED) k.speed = MAX_SPEED;
      } else if (k.driftTime >= cfg.redThreshold) {
        k.boostTimer = Math.max(k.boostTimer, cfg.redDuration);
        if (k.speed < MAX_SPEED) k.speed = MAX_SPEED;
      } else if (k.driftTime >= cfg.orangeThreshold) {
        k.boostTimer = Math.max(k.boostTimer, cfg.orangeDuration);
        if (k.speed < MAX_SPEED) k.speed = MAX_SPEED;
      }
      k.driftTime = 0;
    }
  }

  // ── A4: Trick boost (flag on landing) ──
  if (input.trick && k.y > 0.5 && !k.trickBoosting) {
    k.trickBoosting = true;
  }

  // ── Position ──
  k.x += k.vx * dt;
  k.z += k.vz * dt;

  // ── Ramp elevation & gravity ──
  applyRampGravity(k, dt, track);

  // ── Wall collision ──
  if (track) {
    const wall = checkWallCollision(track.path, k.x, k.z, track.roadWidth / 2);
    if (wall.hit) {
      k.x = wall.pushX;
      k.z = wall.pushZ;

      const dot = k.vx * wall.normalX + k.vz * wall.normalZ;
      if (dot < 0) {
        k.vx -= dot * wall.normalX;
        k.vz -= dot * wall.normalZ;
      }

      k.vx *= 0.9;
      k.vz *= 0.9;

      const projSpeed = Math.sqrt(k.vx * k.vx + k.vz * k.vz);
      const fwdDot = k.vx * Math.cos(k.yaw) + k.vz * Math.sin(k.yaw);
      k.speed = fwdDot >= 0 ? projSpeed : -projSpeed;
    }
  }

  // ── A5: Off-road detection ──
  if (track) {
    const closest = findClosestPoint(track.path, k.x, k.z);
    const halfWidth = track.roadWidth / 2;
    const ratio = Math.abs(closest.lateralOffset) / halfWidth;
    k.offRoad = ratio > config.offroad.lateralThreshold;
  }

  // ── A3: Slipstream / drafting ──
  if (allKarts && allKarts.length > 1) {
    const slipCfg = config.slipstream;
    let inSlipstream = false;

    for (const other of allKarts) {
      if (other.id === k.id) continue;

      const dx = other.x - k.x;
      const dz = other.z - k.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist > slipCfg.distance || dist < 1) continue;

      // Check if kart is behind the other kart (within cone)
      const dirToOther = Math.atan2(dz, dx);
      let angleDiff = dirToOther - k.yaw;
      while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
      while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

      if (Math.abs(angleDiff) < slipCfg.coneAngle) {
        inSlipstream = true;
        break;
      }
    }

    if (inSlipstream && k.slipstreamTimer < slipCfg.chargeTime) {
      k.slipstreamTimer += dt;
      if (k.slipstreamTimer >= slipCfg.chargeTime) {
        // Grant slipstream boost
        k.boostTimer = Math.max(k.boostTimer, slipCfg.boostDuration);
        if (k.speed < MAX_SPEED) k.speed = MAX_SPEED;
        k.slipstreamTimer = 0;
      }
    } else if (!inSlipstream) {
      // Decay slipstream charge when not drafting
      k.slipstreamTimer = Math.max(0, k.slipstreamTimer - dt * 0.5);
    }
  }

  // ── G7: Wrong-way detection ──
  if (track) {
    const closest = findClosestPoint(track.path, k.x, k.z);
    const tp = track.path[closest.index];
    // Dot product of kart facing direction with track tangent
    const facingX = Math.cos(k.yaw);
    const facingZ = Math.sin(k.yaw);
    const dot = facingX * tp.dx + facingZ * tp.dz;
    k.wrongWay = dot < -0.3 && Math.abs(k.speed) > 2;
  }

  return k;
}

// ============================================================
// Ramp gravity helper
// ============================================================

function applyRampGravity(
  k: KartState,
  dt: number,
  track: PhysicsTrackContext | null
): void {
  if (!track) {
    k.y = 0;
    k.vy = 0;
    return;
  }

  const wasInAir = k.y > 1.0;
  const ground = checkRampGround(track.ramps, k.x, k.z);

  if (k.y <= ground.height + 0.05) {
    k.y = ground.height;
    if (ground.onRamp) {
      const vForward = k.vx * ground.forwardX + k.vz * ground.forwardZ;
      k.vy = Math.max(0, vForward * ground.slopeFactor);
    } else {
      k.vy = 0;
    }
  } else {
    k.vy -= GRAVITY * dt;
    k.y += k.vy * dt;
    if (k.y <= ground.height) {
      k.y = ground.height;
      k.vy = 0;
    }
  }

  // Landing boost
  if (wasInAir && k.y <= ground.height + 0.05) {
    k.boostTimer = Math.max(k.boostTimer, RAMP_LANDING_BOOST);
    if (k.speed < MAX_SPEED) k.speed = MAX_SPEED;

    // A4: Apply trick boost on landing
    if (k.trickBoosting) {
      k.boostTimer = Math.max(k.boostTimer, config.specialItems.trickBoostDuration);
      k.trickBoosting = false;
    }
  }

  // Reset trick flag when grounded and not airborne
  if (k.y <= ground.height + 0.05 && !wasInAir) {
    k.trickBoosting = false;
  }
}

// ============================================================
// Default new KartState fields (for creating/resetting karts)
// ============================================================

export function defaultNewKartFields(): Partial<KartState> {
  return {
    driftTime: 0,
    coins: 0,
    slipstreamTimer: 0,
    starTimer: 0,
    shrinkTimer: 0,
    blooperTimer: 0,
    wrongWay: false,
    offRoad: false,
    heldItemBehind: null,
    orbitItems: 0,
    rouletteTimer: 0,
    trickBoosting: false,
  };
}

// ============================================================
// Utility
// ============================================================

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
